import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSearchSuggestions } from '../../hooks/useProducts';
import { Search, TrendingUp } from 'lucide-react';
import './SearchAutocomplete.css';

const SearchAutocomplete = ({ query, onQueryChange, onClose, onSearch }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  
  // Get search suggestions
  const { data: suggestionsData = [], isLoading } = useSearchSuggestions(query);
  const suggestions = Array.isArray(suggestionsData) ? suggestionsData : [];

  useEffect(() => {
    setIsOpen(query.length >= 2);
    setSelectedIndex(-1);
  }, [query]);

  const handleSuggestionClick = (suggestion) => {
    // Use 'q' parameter to match SearchPage expectations
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (query.trim()) {
          onSearch();
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        // Other keys are ignored
        break;
    }
  };

  if (!isOpen || query.length < 2) return null;

  return (
    <div className="search-autocomplete" onKeyDown={handleKeyDown}>
      <div className="autocomplete-header">
        <TrendingUp size={16} />
        <span>{t('searchSuggestions', { defaultValue: 'Search Suggestions' })}</span>
      </div>
      {isLoading ? (
        <div className="autocomplete-loading">
          <div className="loading-spinner" />
          <span>{t('searching', { defaultValue: 'Searching...' })}</span>
        </div>
      ) : suggestions.length > 0 ? (
        <ul className="autocomplete-list" ref={suggestionsRef}>
          {suggestions.slice(0, 8).map((suggestion, index) => (
            <li
              key={index}
              className={`autocomplete-item ${
                index === selectedIndex ? 'selected' : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Search size={16} />
              <span>{suggestion}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="autocomplete-empty">
          <span>{t('noSuggestionsFound', { defaultValue: 'No suggestions found' })}</span>
        </div>
      )}
      {query.trim() && (
        <div className="autocomplete-footer">
          <button
            className="autocomplete-search-all"
            onClick={onSearch}
          >
            {t('searchFor', { defaultValue: 'Search for', query: query })}
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;

